import { Share2, Heart, Phone, MessageCircle } from 'lucide-react';

/**
 * Formats price into readable format (Cr, Lac, or regular)
 * @param {number} price - The price to format
 * @returns {string} Formatted price string
 */
const formatPrice = (price) => {
  if (!price || price === 0) return "NA";
  const numPrice = Number(price);
  
  if (numPrice >= 10000000) {
    // 1 crore or more
    const croreValue = numPrice / 10000000;
    return `₹${croreValue % 1 === 0 ? croreValue.toFixed(0) : croreValue.toFixed(2)}Cr`;
  } else if (numPrice >= 100000) {
    // 1 lac or more
    const lacValue = numPrice / 100000;
    return `₹${lacValue % 1 === 0 ? lacValue.toFixed(0) : lacValue.toFixed(2)}Lac`;
  } else {
    return `₹${numPrice}`;
  }
};

export default function PropertyCard({
  id,
  date,
  premise,
  rent,
  status,
  type,
  subtype,
  area,
  size,
  sqft,
  condition,
  address,
  description,
  brokerage,
  tenant,
  floor,
  contactName,
  contactPhone,
  imageUrl = 'https://citynect1.s3.ap-south-1.amazonaws.com/Image+not+available.png'
}) {
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 w-full">
      <div className="flex flex-col lg:flex-row">
        {/* Image Section */}
       <div className="relative lg:w-1/4 h-48 lg:h-full">
  <img
    src={imageUrl}
    alt={premise}
    className="w-full h-full object-cover"
    onError={(e) => {
      // If image fails to load, use a placeholder or default image
      e.target.src = 'https://citynect1.s3.ap-south-1.amazonaws.com/Image+not+available.png';
      e.target.className = 'w-full h-full object-cover bg-gray-200';
    }}
  />
  
  <div className="absolute bottom-3 left-3 right-3 flex justify-center gap-1">
    {[...Array(6)].map((_, i) => (
      <div
        key={i}
        className={`h-1 rounded-full ${i === 0 ? 'w-4 bg-white' : 'w-1 bg-white/50'}`}
      />
    ))}
  </div>
</div>

        {/* Content Section */}
        <div className="flex-1 p-4 lg:p-5">
          {/* Header */}
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-1">
                <h2 className="text-xl lg:text-2xl font-bold text-gray-900">
                  {premise}
                </h2>
                <div className="flex gap-2 sm:gap-3 sm:ml-auto">
                  <div className="rounded-full text-red-500 font-medium px-2 py-1 border border-red-500 whitespace-nowrap text-xs sm:text-sm">
                    {date}
                  </div>
                  <div className="rounded-full text-blue-600 font-medium px-2 py-1 border border-blue-600 whitespace-nowrap text-xs sm:text-sm">
                    ID: {id}  
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                • <span className="mx-1 sm:mx-2">{type}</span> • <span className="mx-1 sm:mx-2">{size}</span> • <span className="mx-1 sm:mx-2">{subtype}</span>
              </p>
            </div>
            <div className="flex gap-2 ml-4">
              <button className="p-1.5 hover:bg-gray-100 rounded-full transition-colors">
                <Share2 className="w-4 h-4 text-gray-600" />
              </button>
            </div>  
          </div>

          {/* Price and Info Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-4 py-3 border-y border-gray-200">
            <div className="text-center">
              <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                {formatPrice(rent)}
              </div>
              <div className="text-xs text-gray-500 mt-1">Rent</div>
            </div>
            <div className="text-center">
              <div className="text-base sm:text-lg font-semibold text-gray-900">
                {sqft} SqFt
              </div>
              <div className="text-xs text-gray-500 mt-1">Size</div>
            </div>
            <div className="text-center">
              <div className="text-base sm:text-lg font-semibold text-gray-900">
                {condition}
              </div>
              <div className="text-xs text-gray-500 mt-1">Condition</div>
            </div>
            <div className="text-center">
              <div className="text-base sm:text-lg font-semibold text-gray-900">
                {size}
              </div>
              <div className="text-xs text-gray-500 mt-1">Size</div>
            </div>
            <div className="text-center">
              <div className="text-base sm:text-lg font-semibold text-gray-900">
                {status}
              </div>
              <div className="text-xs text-gray-500 mt-1">Status</div>  
            </div>
          </div>

          {/* Property Details - Fixed for mobile */}
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
              <span className="text-[#503196]">⚡</span>
              Property Details
            </h3>
            <div className="flex items-center gap-2">
              <button className="text-gray-400 hover:text-gray-600 text-sm flex-shrink-0 hidden sm:block">
                ←
              </button>
              <p className="text-sm text-gray-600 flex-1 line-clamp-2 min-h-[2.5rem]">
                {description} 
              </p>
              <button className="text-gray-400 hover:text-gray-600 text-sm flex-shrink-0 hidden sm:block">
                →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}